/**
 * 认证接口（公开接口，不带 Bearer、不触发 401 刷新）。
 * 契约：POST /auth/register|login → {user,accessToken,refreshToken}；POST /auth/refresh → {accessToken,...}；
 *       POST /auth/logout → 204。
 */
import { api } from './client.js';
import type { AuthSessionDto, RefreshDto } from './types.js';

export interface RegisterInput {
  email: string;
  password: string;
  name: string;
  /** 学校：与 shared 的 validateRegister 一致，接收字符串；后端负责归一化成 SchoolId */
  school: string;
  /** 是否勾选同意《用户协议》与《隐私政策》（后端强制为 true，否则拒绝注册） */
  consent: boolean;
}

export interface LoginInput {
  email: string;
  password: string;
}

export const authApi = {
  register: (input: RegisterInput): Promise<AuthSessionDto> => api.post<AuthSessionDto>('/auth/register', input, false),
  login: (input: LoginInput): Promise<AuthSessionDto> => api.post<AuthSessionDto>('/auth/login', input, false),
  refresh: (refreshToken: string): Promise<RefreshDto> =>
    api.post<RefreshDto>('/auth/refresh', { refreshToken }, false),
  /** 退出登录：撤销服务端 refresh token；失败不阻塞本地登出（调用方忽略异常即可） */
  logout: (refreshToken: string): Promise<void> => api.post<void>('/auth/logout', { refreshToken }),
  /** 发送/重发注册邮箱验证码（公开接口，不带 Bearer） */
  sendVerifyCode: (email: string): Promise<void> =>
    api.post<void>('/auth/send-verify-code', { email }, false),
  /** 校验邮箱验证码并标记账号已验证（公开接口，不带 Bearer） */
  verifyEmail: (email: string, code: string): Promise<{ verified: boolean }> =>
    api.post<{ verified: boolean }>('/auth/verify-email', { email, code }, false),
  /** 找回密码：发重置验证码（公开接口；后端不暴露邮箱是否已注册） */
  forgotPassword: (email: string): Promise<{ sent: boolean }> =>
    api.post<{ sent: boolean }>('/auth/forgot-password', { email }, false),
  /** 找回密码：验证码 + 新密码 → 重置（公开接口；成功后旧会话全部失效） */
  resetPassword: (email: string, code: string, password: string): Promise<{ reset: boolean }> =>
    api.post<{ reset: boolean }>('/auth/reset-password', { email, code, password }, false),
  /** 自助改密（登录态）：成功后旧会话全部失效，需重新登录 */
  changePassword: (oldPassword: string, newPassword: string): Promise<{ changed: boolean }> =>
    api.post<{ changed: boolean }>('/auth/change-password', { oldPassword, newPassword }),
};